/**
 * O NÚCLEO que todo módulo de área importa, e só ele: `import { ... } from '../nucleo.js'`.
 *
 * POR QUE UM ARQUIVO SÓ PARA ISSO: as áreas são escritas por frentes diferentes, em paralelo. Se
 * cada uma importasse de `cliente.ts`, `resultado.ts` e `escopo.ts` por conta própria, a primeira
 * mudança de nome num desses arquivos quebraria cinco áreas em silêncio até o typecheck. Com um
 * ponto de entrada, o contrato das áreas mora num lugar, e é este.
 *
 * O `clienteDe` sai daqui para as áreas e NÃO sai pelo `index.ts`: a tela nunca recebe o cliente.
 */

// A fonte e o acesso interno ao cliente e ao token.
import { clienteDe, tokenDe, type FonteDeDados } from './cliente.js';
// O formato de resposta, e os construtores dele.
import { falha, lerErroDeFuncao, mensagemDe, sucesso, type Resultado } from './resultado.js';

export { clienteDe, tokenDe, type FonteDeDados } from './cliente.js';
export {
  executar,
  falha,
  sucesso,
  mensagemDe,
  type Resultado,
  type ErroDeDados,
  type CorpoDeErro,
} from './resultado.js';
export { comEscopo, registroComLead, caminhoArquivosCliente, CAMINHO_ARQUIVOS_ADMIN, type Escopo } from './escopo.js';
export type * from './tipos.js';

/**
 * O único bucket de Storage que o admin legado usa (`sb.storage.from('materiais')`, 21 chamadas em
 * `admin.html`, medido em 19/09/2026). Nome numa constante para nenhuma área digitar diferente.
 */
export const BUCKET_MATERIAIS = 'materiais';

/** O que o legado passa a `functions.invoke`: corpo JSON e, numa chamada só, cabeçalho próprio. */
export interface OpcoesDeFuncao {
  // O corpo em JSON, sempre um objeto no legado.
  readonly body?: Record<string, unknown>;
  // Cabeçalhos extras; só `criar-usuario-sistema` manda (`admin.html:8897`).
  readonly headers?: Record<string, string>;
}

/**
 * Invoca uma Edge Function e devolve `Resultado`, com o corpo de erro já lido.
 *
 * SUCESSO NÃO QUER DIZER QUE A FUNÇÃO ACEITOU: várias respondem 200 com `{ ok: false }` ou
 * `{ error }` no corpo (regra de negócio, e não falha de sistema, como explica `admin.html:8915`).
 * Isso volta em `dados`, como veio, e a tela decide igual ao legado. Só status fora de 2xx e rede
 * caída viram `ok: false`.
 */
export async function invocarFuncao<T>(
  fonte: FonteDeDados,
  nome: string,
  opcoes: OpcoesDeFuncao = {},
): Promise<Resultado<T>> {
  try {
    // A chamada leva o token da sessão pelo `fetchWithAuth` do cliente (ver `cliente.ts`).
    const { data, error } = await clienteDe(fonte).functions.invoke(nome, {
      // Só passa o que veio, para não mandar `body: undefined` a quem não espera corpo.
      ...(opcoes.body !== undefined ? { body: opcoes.body } : {}),
      ...(opcoes.headers !== undefined ? { headers: opcoes.headers } : {}),
    });
    // Status fora de 2xx ou rede: a mensagem genérica, o status e o corpo lido uma vez.
    if (error) return await lerErroDeFuncao(error as { message: string; context?: unknown });
    // Resposta 2xx: o corpo como veio, com o tipo que a área afirma.
    return sucesso(data as T);
  } catch (lancado) {
    // Exceção antes de qualquer resposta: vira falha, e não sobe para a tela.
    return falha(mensagemDe(lancado));
  }
}

/**
 * O cabeçalho `Authorization` com o token da sessão, para a chamada do legado que o manda
 * explícito (`criar-usuario-sistema`, `admin.html:8897`). Sem sessão, nenhum cabeçalho: o cliente
 * põe o dele, como faria de qualquer jeito.
 */
export async function cabecalhoDeAutorizacao(fonte: FonteDeDados): Promise<Record<string, string>> {
  const token = await tokenDe(fonte);
  return token ? { Authorization: `Bearer ${token}` } : {};
}
